import { Component, ElementRef, ViewChild, AfterViewInit, OnDestroy, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { AppMenuitem } from './app.menuitem';
import { UserService } from '@/core/service/user.service';
import { User } from '@/core/service/models/user';
import { environment } from 'src/environments/environment';
import { LayoutService } from '@/layout/service/layout.service';
import { TOI_CONSULTA_ROLES } from '@/features/toi/utils/toi-permissao.util';


@Component({
    selector: '[app-menu]',
    standalone: true,
    imports: [CommonModule, AppMenuitem, RouterModule],
    template: `
        <!-- Menu horizontal com setas de navegação -->
        <div *ngIf="isHorizontal()" class="relative flex items-center w-full">
            <!-- Seta esquerda -->
            <button
                *ngIf="showLeftArrow"
                (click)="scrollLeft()"
                class="absolute left-0 z-[5] flex items-center justify-center w-8 h-8 rounded-full bg-white/90 hover:bg-white shadow-md border border-gray-200 transition-all duration-200"
                style="top: 50%; transform: translateY(-50%); pointer-events: auto;"
                aria-label="Scroll left"
                type="button">
                <i class="pi pi-chevron-left text-gray-700 text-sm"></i>
            </button>

            <!-- Container do menu com scroll -->
            <div
                #menuWrapper
                class="flex-1 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]"
                [class.pl-10]="showLeftArrow"
                [class.pr-10]="showRightArrow"
                (scroll)="onScroll()"
                style="overflow-x: auto; overflow-y: visible; min-width: 0; position: relative;">
                <ul class="layout-menu" style="position: relative;">
                    <ng-container *ngFor="let item of filteredModel; let i = index">
                        <li app-menuitem *ngIf="!item.separator" [item]="item" [index]="i" [root]="true"></li>
                        <li *ngIf="item.separator" class="menu-separator"></li>
                    </ng-container>
                </ul>
            </div>

            <!-- Seta direita -->
            <button
                *ngIf="showRightArrow"
                (click)="scrollRight()"
                class="absolute right-0 z-[5] flex items-center justify-center w-8 h-8 rounded-full bg-white/90 hover:bg-white shadow-md border border-gray-200 transition-all duration-200"
                style="top: 50%; transform: translateY(-50%); pointer-events: auto;"
                aria-label="Scroll right"
                type="button">
                <i class="pi pi-chevron-right text-gray-700 text-sm"></i>
            </button>
        </div>

        <!-- Menu vertical (modo não horizontal) -->
        <ul *ngIf="!isHorizontal()" class="layout-menu">
            <ng-container *ngFor="let item of filteredModel; let i = index">
                <li app-menuitem *ngIf="!item.separator" [item]="item" [index]="i" [root]="true"></li>
                <li *ngIf="item.separator" class="menu-separator"></li>
            </ng-container>
        </ul>
    `
})
export class AppMenu implements AfterViewInit, OnDestroy {

    roles: string[] = [];
    filteredModel: any[] = [];
    public sigosMvcUrl: string = environment.sigosMvcUrl;

    @ViewChild('menuWrapper') menuWrapper!: ElementRef<HTMLDivElement>;

    showLeftArrow = false;
    showRightArrow = false;

    private resizeObserver?: ResizeObserver;
    private scrollCheckTimeout?: any;

    constructor(
        private userTokenService: UserService,
        private layoutService: LayoutService
    ) {
        this.roles = this.userTokenService.getRoles();
        this.filteredModel = this.filterMenuItems(this.model);
    }

    ngAfterViewInit() {
        if (this.isHorizontal() && this.menuWrapper) {
            setTimeout(() => {
                this.checkScrollButtons();
                this.setupResizeObserver();
            }, 0);
        }
    }

    ngOnDestroy() {
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
        }
        if (this.scrollCheckTimeout) {
            clearTimeout(this.scrollCheckTimeout);
        }
    }

    @HostListener('window:resize', ['$event'])
    onResize() {
        if (this.isHorizontal()) {
            if (this.scrollCheckTimeout) {
                clearTimeout(this.scrollCheckTimeout);
            }
            this.scrollCheckTimeout = setTimeout(() => {
                this.checkScrollButtons();
            }, 100);
        }
    }

    isHorizontal(): boolean {
        return this.layoutService.isHorizontal();
    }

    setupResizeObserver() {
        if (typeof ResizeObserver !== 'undefined' && this.menuWrapper) {
            this.resizeObserver = new ResizeObserver(() => {
                this.checkScrollButtons();
            });
            this.resizeObserver.observe(this.menuWrapper.nativeElement);
        }
    }

    onScroll() {
        this.checkScrollButtons();
    }

    checkScrollButtons() {
        if (!this.menuWrapper || !this.isHorizontal()) {
            return;
        }

        const element = this.menuWrapper.nativeElement;
        const scrollLeft = element.scrollLeft;
        const scrollWidth = element.scrollWidth;
        const clientWidth = element.clientWidth;

        this.showLeftArrow = scrollLeft > 0;
        this.showRightArrow = scrollLeft < (scrollWidth - clientWidth - 1);
    }

    scrollLeft() {
        if (this.menuWrapper) {
            const element = this.menuWrapper.nativeElement;
            const scrollAmount = element.clientWidth * 0.8;
            element.scrollBy({
                left: -scrollAmount,
                behavior: 'smooth'
            });
        }
    }

    scrollRight() {
        if (this.menuWrapper) {
            const element = this.menuWrapper.nativeElement;
            const scrollAmount = element.clientWidth * 0.8;
            element.scrollBy({
                left: scrollAmount,
                behavior: 'smooth'
            });
        }
    }

    hasAccess(item: any): boolean {
        if (!item.roles || !item.roles.length) {
            return true;
        }
        return this.roles.some(role => item.roles.includes(role));
    }

    filterMenuItems(items: any[]): any[] {
        return items
            .map(item => {
                // Handle separators
                if (item.separator) {
                    return item;
                }

                // Clone the item to avoid mutating the original
                const clonedItem = { ...item };

                // If the item has sub-items, filter them recursively
                if (clonedItem.items && clonedItem.items.length > 0) {
                    clonedItem.items = this.filterMenuItems(clonedItem.items);

                    // If no sub-items remain after filtering, check if parent has direct access
                    if (clonedItem.items.length === 0 && !this.hasAccess(clonedItem)) {
                        return null;
                    }

                    // If parent doesn't have direct access but has accessible children, keep it
                    if (clonedItem.items.length > 0) {
                        return clonedItem;
                    }

                    return null;
                }

                // For items without sub-items, check direct access
                return this.hasAccess(clonedItem) ? clonedItem : null;
            })
            .filter(item => item !== null);
    }

    model: any[] = [
        {
            label: 'DASHBOARDS',
            icon: 'pi pi-home',
            items: [

                {
                    label: 'Dashboard de Fraudes',
                    routerLink: ['/dashboard-banking'],
                    roles: ['P_MAX', 'DASH_FRAUDES_CON']
                },
                {
                    label: 'Dashboard de Integrações',
                    routerLink: ['/monitor-dashboard'],
                    roles: ['P_MAX', 'MONITOR_INTERFACE_CON'],
                },
                {
                    label: 'Dashboard de Penalidades',
                    routerLink: ['/dashboard-penalidades'],
                    roles: ['P_MAX', 'WFM_PENALIDADE_CON']
                },
                {
                    label: 'Dashboard de Produção Dia',
                    routerLink: ['/producao-diaria'],
                    roles: ['P_MAX']
                },
                {
                    label: 'Dashboard de Produção Diária',
                    routerLink: ['/'],
                    roles: ['P_MAX', 'HOME_CON']
                },
                {
                    label: 'Visão Computacional',
                    routerLink: ['/dashboard-visao-computacional/lista'],
                    roles: ['P_MAX', 'REL_AQUARELLE_DASHBOARD_CON']
                },
                {
                    label: 'Dashboard Leitura',
                    routerLink: ['/dashboard-leitura'],
                    roles: ['P_MAX'] //'REL_LEITURA_DASHBOARD_CON'
                },
            ]
        },
        {
            label: 'CONFIGURAÇÕES',
            icon: 'pi pi-cog',
            roles: ['P_MAX', 'INTERFACES_DOMINIO_CON'],
            items: [
                {
                    label: 'Administrativo',
                    roles: ['P_MAX', 'AJUSTE_PONTO_CON','SEG_EMPRESA_SPE_CON','MONITOR_INTERFACE_CON'],

                    items: [
                        {
                            label: 'Ajuste de Ponto',
                            routerLink: ['/ajuste-ponto'],
                            roles: ['P_MAX', 'AJUSTE_PONTO_CON'],
                        },
                        {
                            label: 'Empresa SPE',
                            routerLink: ['/empresa-spe'],
                            roles: ['P_MAX', 'SEG_EMPRESA_SPE_CON'],
                        },

                    ]
                },
                {
                    label: 'Característica de Equipes',
                    routerLink: ['/caracteristica-equipe'],
                    roles: ['P_MAX', 'INTERFACES_DOMINIO_CON'],
                },
                {
                    label: 'Cargos',
                    routerLink: ['/cargo'],
                    roles: ['P_MAX', 'MXL_CARGO_CON'],
                },
                {
                    label: 'Centro de Custo',
                    routerLink: ['/centro-de-custo/lista'],
                    roles: ['P_MAX', 'INTERFACES_DOMINIO_CON']
                },
                {
                    label: 'Estrutura Organizacional',
                    routerLink: ['/estrutura-organizacional'],
                    roles: ['P_MAX', 'ORG_HIERARQUIA_CON']
                },
                {
                    label: 'Feriados',
                    roles: ['P_MAX', 'INTERFACES_DOMINIO_CON'],
                    items: [
                        {
                            label: 'Feriados Nacionais',
                            routerLink: ['/feriados/nacionais-lista'],
                            roles: ['P_MAX', 'INTERFACES_DOMINIO_CON'],
                        },
                        {
                            label: 'Feriados por Localidade',
                            routerLink: ['/feriados/municipais-lista'],
                            roles: ['P_MAX', 'INTERFACES_DOMINIO_CON'],
                        },
                    ]
                },
                {
                    label: 'Localidades',
                    roles: ['P_MAX', 'INTERFACES_DOMINIO_CON'],
                    items: [
                        {
                            label: 'Localidades',
                            routerLink: ['/localidades/localidade-lista'],
                            roles: ['P_MAX', 'INTERFACES_DOMINIO_CON'],
                        },
                        {
                            label: 'Pavimentação',
                            routerLink: ['/localidades/pavimentacao-lista'],
                            roles: ['P_MAX', 'INTERFACES_DOMINIO_CON'],
                        },
                        {
                            label: 'Ramos de Atividade',
                            routerLink: ['/localidades/ramo-atividade-lista'],
                            roles: ['P_MAX', 'INTERFACES_DOMINIO_CON'],
                        },
                        {
                            label: 'Tipos de Logradouro',
                            routerLink: ['/localidades/logradouro-lista'],
                            roles: ['P_MAX', 'INTERFACES_DOMINIO_CON'],
                        },
                    ]
                },
                {
                    label: 'Medidores',
                    roles: ['P_MAX', 'INTERFACES_DOMINIO_CON'],
                    items: [
                        {
                            label: 'Dimensionamentos de Medidor',
                            routerLink: ['/medidores/dimensionamento-de-medidor-lista'],
                            roles: ['P_MAX', 'INTERFACES_DOMINIO_CON'],
                        },
                        {
                            label: 'Marcas de Medidor',
                            routerLink: ['/medidores/marca-de-medidor-lista'],
                            roles: ['P_MAX', 'INTERFACES_DOMINIO_CON'],
                        },
                        {
                            label: 'Tipos de Medidor',
                            routerLink: ['/medidores/tipo-de-medidor-lista'],
                            roles: ['P_MAX', 'INTERFACES_DOMINIO_CON'],
                        }
                    ]
                },
                {
                    label: 'Serviços',
                    roles: ['P_MAX', 'INTERFACES_DOMINIO_CON'],
                    items: [
                        {
                            label: 'Característica de Serviços',
                            routerLink: ['/caracteristica-servico/lista'],
                            roles: ['P_MAX', 'TIPO_ATIVIDADE_CON']
                        },
                        {
                            label: 'Classificação de Serviços',
                            routerLink: ['/classificacao-servico/lista'],
                            roles: ['P_MAX', 'INTERFACES_DOMINIO_CON'],
                        },
                        {
                            label: 'Fluxo de Serviços',
                            routerLink: ['/fluxo-servicos'],
                            roles: ['P_MAX', 'FLUXO_ATIVIDADE_CON'],
                        },
                        {
                            label: 'Status de Serviços',
                            routerLink: ['/status-servico'],
                            roles: ['P_MAX', 'WFM_ATIVIDADE_STATUS_CON'],
                        },
                    ]
                },
                {
                    label: 'Tabelas de Baixa',
                    roles: ['P_MAX', 'INTERFACES_DOMINIO_CON'],
                    items: [
                        {
                            label: 'Ocorrências',
                            routerLink: ['/tabelas-de-baixa/ocorrencia-lista'],
                            roles: ['P_MAX', 'INTERFACES_DOMINIO_CON'],
                        },
                        {
                            label: 'Perguntas',
                            routerLink: ['/tabelas-de-baixa/pergunta-lista'],
                            roles: ['P_MAX', 'INTERFACES_DOMINIO_CON'],
                        },
                        {
                            label: 'Reclamações',
                            routerLink: ['/tabelas-de-baixa/reclamacao-lista'],
                            roles: ['P_MAX', 'INTERFACES_DOMINIO_CON'],
                        },
                        {
                            label: 'Respostas',
                            routerLink: ['/tabelas-de-baixa/resposta-lista'],
                            roles: ['P_MAX', 'INTERFACES_DOMINIO_CON'],
                        },
                        {
                            label: 'Resultados',
                            routerLink: ['/tabelas-de-baixa/resultado-lista'],
                            roles: ['P_MAX', 'INTERFACES_DOMINIO_CON'],
                        },
                    ]
                },
                  {
                    label: 'Checklist',
                    roles: ['P_MAX', 'CHECKLIST_CON'],
                    items: [
                        {
                            label: 'Controle de Checklist',
                            routerLink: ['/checklist/templates'],
                            roles: ['P_MAX', 'CHECKLIST_CON'],
                        },
                        {
                            label: 'Itens de Checklist',
                            routerLink: ['/checklist/template/layout'],
                            roles: ['P_MAX', 'CHECKLIST_CON'],
                        },
                    ]
                },
                {
                    label: 'SPIA Parametrização',
                    routerLink: ['/spia/parametrizacao'],
                    roles: ['P_MAX', 'SPIA_PARAMETRIZACAO_CON'],
                },
                {
                    label: 'Penalidade',
                    routerLink: ['/penalidade/lista'],
                    roles: ['P_MAX', 'PARAM_COPASA_CON'],
                }
            ]
        },
        //{ separator: true },
        {
            label: 'EQUIPES',
            icon: 'pi pi-users',
            items: [
                {
                    label: 'Configuração de Equipes',
                    routerLink: ['/configuracao-equipe/lista'],
                    roles: ['P_MAX', 'WFM_EQUIPE_CON'],
                },
                {
                    label: 'Configuração de Serviços',
                    routerLink: ['/configuracao-servico'],
                    roles: ['P_MAX', 'CONFIGURACAO_SERVICOS_CON'],
                },
                {
                    label: 'Importação de Empregados',
                    routerLink: ['/importacao/empregados'],
                    roles: ['P_MAX', 'IMP_DADOS_FUNCIONARIO_TERCEIRIZADO_CON'],
                },
                {
                    label: 'Importação de Escalas',
                    routerLink: ['/importacao/escala'],
                    roles: ['P_MAX', 'IMP_ESCALA_DISPONIBILIDADE_CON'],
                },
                {
                    label: 'Importação de Situação do Empregado',
                    routerLink: ['/importacao/situacao'],
                    roles: ['P_MAX', 'IMP_DADOS_SITUACAO_FOLHA_CON'],
                },
                {
                    label: 'Importação de Turnos',
                    routerLink: ['/importacao/turno'],
                    roles: ['P_MAX', 'IMP_DADOS_TURNO_HORARIO_CON'],
                },
                {
                    label: 'Planejamento de Escalas',
                    routerLink: ['/planejamento-escala'],
                    roles: ['P_MAX', 'ESCALA_PLANEJAMENTO_CON'],
                },
                {
                    label: 'Relação de Empregados',
                    routerLink: ['/relacao-funcionario/list'],
                    roles: ['P_MAX', 'ACESSO_RELACAO_FUNCIONARIOS_CON'],
                },
                {
                    label: 'Relação de Veículos',
                    routerLink: ['/relacao-veiculos'],
                    roles: ['P_MAX', 'RELACAO_VEICULOS_CON'],
                },
            ]
        },
        //{ separator: true },
        {
            label: 'PROGRAMAÇÃO',
            icon: 'pi pi-fw pi-sliders-h',
            items: [
                {
                    label: 'Atribuição de Serviços',
                    routerLink: ['/ajuste-programacao'],
                    roles: ['P_MAX', 'WFM_DESPACHO_ACOMPANHAMENTO_CON'],
                },
                {
                    label: 'Atribuição de Leitura',
                    routerLink: ['/ajuste-programacao-leitura'],
                    roles: ['P_MAX'], //'WFM_DESPACHO_ACOMPANHAMENTO_CON'
                },
                {
                    label: 'Parametrização de Carga',
                    routerLink: ['/carga-leiturista'],
                    roles: ['P_MAX'],//'WFM_DESPACHO_ACOMPANHAMENTO_CON'
                },
                {
                    label: 'Cenários de Priorização',
                    routerLink: ['/priorizacao-cenario'],
                    roles: ['P_MAX', 'WFM_CENARIO_CON'],
                },
                {
                    label: 'Serviços Pendentes',
                    routerLink: ['/servico-pendente'],
                    roles: ['P_MAX', 'SERVICOS_PENDENTES_CON'],
                },
            ]
        },
        //{ separator: true },
        {
            label: 'MATERIAIS',
            icon: 'pi pi-fw pi-hammer',
            items: [
                {
                    label: 'Consulta de Saldo',
                    routerLink: ['/material-consulta-saldo-sap'],
                    roles: ['P_MAX', 'MAT_SALDO_SAP_CON'],
                },
                {
                    label: 'Controle de Estoque',
                    routerLink: ['/material-estoque'],
                    roles: ['P_MAX', 'MATERIAL_ESTOQUE_CON'],
                },
                {
                    label: 'Grupo de Materiais',
                    routerLink: ['/grupo-materiais'],
                    roles: ['P_MAX', 'MATERIAL_GRUPO_CON'],
                },

                {
                    label: 'Kits de Materiais',
                    routerLink: ['/material-kit'],
                    roles: ['P_MAX', 'MATERIAL_KIT_CON'],
                },
                {
                    label: 'Materiais Aplicados',
                    routerLink: ['/materiais-aplicados'],
                    roles: ['P_MAX', 'MATERIAIS_APLICADOS_CON'],
                },
                {
                    label: 'Monitor de Materiais',
                    routerLink: ['/material-fluxo-sap'],
                    roles: ['P_MAX', 'MONITORAMENTO_MAT_CON'],
                },
                {
                    label: 'Operação de Materiais',
                    routerLink: ['/material-operacao-spe'],
                    roles: ['P_MAX', 'MAT_MATERIAL_OPERACAO_CON'],
                },
                {
                    label: 'Relação de Depósitos',
                    routerLink: ['/deposito'],
                    roles: ['P_MAX', 'MAT_MATERIAL_OPERACAO_CON'],
                },
                {
                    label: 'Relação de Materiais',
                    routerLink: ['/material'],
                    roles: ['P_MAX', 'MATERIAL_CON'],
                },
            ]
        },
        //{ separator: true },
        {
            label: 'MONITORAMENTO',
            icon: 'pi pi-fw pi-list-check',
            items: [
                {
                    label: 'Diagrama de Gantt',
                    routerLink: ['/gantt'],
                    roles: ['P_MAX', 'AGENDADOR_ESCALA_CON'],
                },
                {
                    label: 'Diagrama de Gantt - Leitura',
                    routerLink: ['/gantt-leitura'],
                    roles: ['P_MAX'], //'AGENDADOR_ESCALA_CON'
                },
                {
                    label: 'Monitor de Integrações',
                    routerLink: ['/monitor-integracao'],
                    roles: ['P_MAX', 'LOG_CRITICA_CON'],
                },
                {
                    label: 'Produção do Dia',
                    routerLink: ['/producao-dia'],
                    roles: ['P_MAX', 'REL_CONTROLE_PRODUCAO_CON'],
                },
                {
                    label: 'Relação de Serviços',
                    routerLink: ['/atividade-list'],
                    roles: ['P_MAX', 'RELACAO_ATIVIDADE_CON'],
                },
                {
                    label: 'Relação de Leituras',
                    routerLink: ['/atividade-leitura'],
                    roles: ['P_MAX'], //'ACESSO_RELATORIO_CON'
                },
                {
                    label: 'Termo de Ocorrência de Irregularidade',
                    routerLink: ['/toi/controle-toi'],
                    roles: [...TOI_CONSULTA_ROLES],
                },
                {
                    label: 'Tratamento de Lotes / Correios',
                    routerLink: ['/toi/tratamento-lote'],
                    roles: [...TOI_CONSULTA_ROLES],
                },
            ]
        },


        //{ separator: true },
        {
            label: 'GESTÃO',
            icon: 'pi pi-fw pi-chart-bar',
            items: [
                {
                    label: 'Atendimento',
                    icon: 'pi pi-fw pi-briefcase',
                    roles: ['P_MAX', 'GESTAO_ATENDIMENTO_CONSOLIDADO_CON','GESTAO_ATENDIMENTO_TIPO_SERVICO_CON','REL_DEMANDA_EQUIPE_CON'],
                    items: [
                        {
                            label: 'Atendimento Consolidado',
                            routerLink: ['/atendimento-consolidado/lista'],
                            roles: ['P_MAX', 'GESTAO_ATENDIMENTO_CONSOLIDADO_CON'],
                        },
                        {
                            label: 'Atendimento por Tipo de Serviço',
                            routerLink: ['/atendimento-tipo-servico/lista'],
                            roles: ['P_MAX', 'GESTAO_ATENDIMENTO_TIPO_SERVICO_CON'],
                        },
                        {
                            label: 'Demanda de Equipes',
                            routerLink: ['/demandas-equipe/lista'],
                            roles: ['P_MAX', 'REL_DEMANDA_EQUIPE_CON'],
                        },
                    ]
                },
                {
                    label: 'Controle',
                    icon: 'pi pi-fw pi-briefcase',
                    roles: ['P_MAX', 'HOME_CON'],
                    items: [
                        {
                            label: 'Produção Mensal',
                            routerLink: ['/producao-mensal'],
                            roles: ['P_MAX', 'REL_PRODUCAO_MENSAL_CON'],
                        },
                        {
                            label: 'Tempo de Atendimento',
                            routerLink: ['/tempo-atendimento'],
                            roles: ['P_MAX', 'REL_TEMPO_ATENDIMENTO_CON'],
                        },
                        {
                            label: 'Tempo Padrão',
                            routerLink: ['/tempo-padrao'],
                            roles: ['P_MAX', 'ACESSO_RELATORIO_CON', 'REL_TEMPO_PADRAO_CON']
                        },
                        {
                            label: 'Tempo de Execução Negativo',
                            routerLink: ['/tempo-execucao-negativo'],
                            roles: ['P_MAX', 'TEMPO_NEGATIVO_CON'],
                        },
                    ]
                },
                {
                    label: ' Índices',
                    icon: 'pi pi-fw pi-briefcase',
                    roles: ['P_MAX', 'REL_INDICE_CONSOLIDADO_CON','GESTAO_ATENDIMENTO_INDICE_CON','','',''],
                    items: [
                        {
                            label: 'Índices de Atendimento',
                            routerLink: ['/indices-atendimento'],
                            roles: ['P_MAX', 'GESTAO_ATENDIMENTO_INDICE_CON'],
                        },
                        {
                            label: 'Índices Consolidados',
                            routerLink: ['/indices-consolidados'],
                            roles: ['P_MAX', 'REL_INDICE_CONSOLIDADO_CON'],
                        },
                        {
                            label: 'Índice de Programação',
                            routerLink: ['/indices-programacao'],
                            roles: ['P_MAX', 'GESTAO_DESPACHO_CON'],
                        },
                        {
                            label: 'Índices de Tempo',
                            routerLink: ['/indices-tempo-geral'],
                            roles: ['P_MAX', 'REL_INDICADOR_TEMPO_CON'],
                        },
                        {
                            label: 'Índices de Tempo por Disponibilidade',
                            routerLink: ['/indice-tempo'],
                            roles: ['P_MAX', 'REL_INDICADOR_TEMPO_DISP_CON'],
                        },

                    ]
                },
                {
                    label: 'Relatórios',
                    icon: 'pi pi-fw pi-briefcase',
                    roles: ['P_MAX', 'REL_EXTENSAO_HORARIO_CON','WFM_ATIVIDADE_REGISTROS_CON','REL_PARALISACAO_EQUIPE_CON','REL_PARALISACAO_SERVICO_CON','REL_PENDENCIA_SERVICOS_CON','WFM_EMPREGADO_REGISTRO_CON','REL_FUNCIONARIO_RDA_CON','REL_SEGURANCA_TRABALHO_CON'],
                    items: [
                        {
                            label: 'Extensão de Horário',
                            routerLink: ['/extensao-horario'],
                            roles: ['P_MAX', 'REL_EXTENSAO_HORARIO_CON'],
                        },
                        {
                            label: 'Paralisação de Equipes',
                            routerLink: ['/paralisacao-equipes'],
                            roles: ['P_MAX', 'REL_PARALISACAO_EQUIPE_CON'],
                        },
                        {
                            label: 'Paralisação de Serviços',
                            routerLink: ['/paralisacao-servico'],
                            roles: ['P_MAX', 'REL_PARALISACAO_SERVICO_CON'],
                        },
                        {
                            label: 'Pendência de Serviços',
                            routerLink: ['/pendencia-servicos'],
                            roles: ['P_MAX', 'REL_PENDENCIA_SERVICOS_CON'],
                        },
                        {
                            label: 'Registros de Empregados',
                            routerLink: ['/registros-empregados'],
                            roles: ['P_MAX', 'WFM_EMPREGADO_REGISTRO_CON'],
                        },
                        {
                            label: 'Relatório de Atividades',
                            routerLink: ['/relatorio-atividades'],
                            roles: ['P_MAX', 'REL_FUNCIONARIO_RDA_CON'],
                        },
                        {
                            label: 'Relatório de Manobras',
                            routerLink: ['/manobras'],
                            roles: ['P_MAX', 'MANOBRAS_CON'],
                        },
                        {
                            label: 'Relatório Segurança do Trabalho',
                            routerLink: ['/seguranca-trabalho'],
                            roles: ['P_MAX'],//'REL_SEGURANCA_TRABALHO_CON'
                        },
                    ]
                },
                {
                    label: 'Serviços',
                    icon: 'pi pi-fw pi-briefcase',
                    roles: ['P_MAX', 'ACESSO_RELATORIO_CON','REL_SERVICOS_APLICADOS_CON','GESTAO_ATENDIMENTO_TIPO_SERVICO_CON','',''],
                    items: [
                        {
                            label: 'Serviços Abertos',
                            routerLink: ['/servicos-abertos'],
                            roles: ['P_MAX', 'ACESSO_RELATORIO_CON'],
                        },
                        {
                            label: 'Serviços Aplicados',
                            routerLink: ['/servicos-aplicados'],
                            roles: ['P_MAX', 'REL_SERVICOS_APLICADOS_CON'],
                        },
                        {
                            label: 'Serviços Encerrados',
                            routerLink: ['/servicos-encerrados'],
                            roles: ['P_MAX', 'ACESSO_RELATORIO_CON'],
                        },
                        {
                            label: 'Leituras Encerradas',
                            routerLink: ['/servicos-encerrados-leitura'],
                            roles: ['P_MAX'], //'ACESSO_RELATORIO_CON'
                        },
                        {
                            label: 'Serviços por Equipe',
                            routerLink: ['/servicos-equipe'],
                            roles: ['P_MAX', 'GESTAO_ATENDIMENTO_TIPO_SERVICO_CON'],
                        },
                        {
                            label: 'Visitas por Serviço',
                            routerLink: ['/visitas-servico'],
                            roles: ['P_MAX', 'REL_VISITAS_POR_SERVICO_CON'],
                        },
                    ]
                },
            ]
        },
        //{ separator: true },
        {
            label: 'USUÁRIO',
            icon: 'pi pi-fw pi-user',
            roles: ['P_MAX', 'CONFIGURACAO_LICENCA_CON','ACESSO_USUARIO_LICENCA_CON','WFM_MENSAGEM_POPUP_CON','ACESSO_PERFIL_CON','ACESSO_ROTINA_CON','ACESSO_USUARIO_CON','MOTIVO_AUSENCIA_CON'],
            items: [
                {
                    label: 'Configuração de Licenças',
                    routerLink: ['/configuracao-licenca/lista'],
                    roles: ['P_MAX', 'CONFIGURACAO_LICENCA_CON'],
                },
                {
                    label: 'Controle de Licenças',
                    routerLink: ['/controle-licenca/lista'],
                    roles: ['P_MAX', 'ACESSO_USUARIO_LICENCA_CON'],
                },
                {
                    label: 'Mensagens aos Usuários',
                    routerLink: ['/mensagens-usuario'],
                    roles: ['P_MAX', 'WFM_MENSAGEM_POPUP_CON'],
                },
                {
                    label: 'Motivo Ausência',
                    routerLink: ['/usuario-motivo-ausencia/lista'],
                    roles: ['P_MAX', 'MOTIVO_AUSENCIA_CON'],
                },
                {
                    label: 'Relação de Perfil',
                    routerLink: ['/perfil/lista'],
                    roles: ['P_MAX', 'ACESSO_PERFIL_CON'],
                },
                {
                    label: 'Relação de Permissões',
                    routerLink: ['/rotina/lista'],
                    roles: ['P_MAX', 'ACESSO_ROTINA_CON'],
                },
                {
                    label: 'Relação de Usuários',
                    routerLink: ['/usuario/lista'],
                    roles: ['P_MAX', 'ACESSO_USUARIO_CON'],
                },
            ]
        },
        //{ separator: true },
        {
            label: 'AJUDA',
            icon: 'pi pi-fw pi-question-circle',
            roles: ['P_MAX', 'HOME_CON'],

            items: [
                {
                    label: 'Controle de Versões',
                    routerLink: ['/controle-versao/lista'],
                    roles: ['P_MAX', 'HOME_CON'],
                },
                {
                    label: 'Documentação',
                    routerLink: 'help',
                    roles: ['P_MAX', 'HOME_CON'],
                },
                {
                    label: 'Retornar para a versão antiga',
                    url: this.sigosMvcUrl,
                    target: '_blank',
                    roles: ['P_MAX', 'HOME_CON'],
                },
            ]
        },
        //{ separator: true },

    ];
}
